import { useEffect, useState } from "react";
import CryptoJS from "crypto-js";
import BaseEndpoint from "../../endpoints/baseEndpoint";
import useAuthUser from "../../hooks/useAuthUser";
import { useHistory } from "react-router-dom";

// Import type-specific question components for spesial-potential-test
import QuestionStandard from "./specialPotential/questionStandard";
import QuestionDimensional from "./specialPotential/questionDimensional";
import QuestionPersonality from "./specialPotential/questionPersonality";
import QuestionKraeplin from "./specialPotential/questionKraeplin";
import QuestionDrawing from "./specialPotential/questionDrawing";
import QuestionSPTPotensiKerja from "./specialPotential/questionSPTPotensiKerja";

const getStoredAnswer = () => {
    const raw = localStorage.getItem("answer");
    if (!raw) return null;
    try {
        const decrypted = CryptoJS.AES.decrypt(raw, "secret").toString(
            CryptoJS.enc.Utf8
        );
        return JSON.parse(decrypted);
    } catch (e) {
        return null;
    }
};

const getStoredMapping = () => {
    const raw = localStorage.getItem("mapping");
    if (!raw) return null;
    try {
        const decrypted = CryptoJS.AES.decrypt(raw, "secret").toString(
            CryptoJS.enc.Utf8
        );
        return JSON.parse(decrypted);
    } catch (e) {
        return null;
    }
};

export default function Question({ session, current, countdown, nextSession }) {
    const router = useHistory();
    const { user } = useAuthUser();
    const [answerData, setAnswerData] = useState(() => getStoredAnswer());
    const [mappingData, setMappingData] = useState(() => getStoredMapping());
    useEffect(() => {
        if (current.data.session === null) return;
        if (countdown.duration === 0) {
            const currentSession = session.data[current.data.session];
            const isKraeplin = getPsychotestType() === 'kraeplin';
            const inkolom = currentSession?.inkolom || 0;

            // For Kraeplin with active per-column timer, column auto-advance and countdown reset are handled inside QuestionKraeplin
            if (isKraeplin && inkolom > 0) {
                return;
            }

            if (session.data[current.data.session]?.type === "exam") {
                finishing();
            } else {
                nextSession();
            }
        }
    }, [countdown.duration, current.data.soal]);

    useEffect(() => {
        if (!session.data || session.data.length === 0) return;
        if (session.data[current.data.session]?.type !== "exam") return;

        let maps = mappingData ? { ...mappingData } : {};
        session.data[current.data.session].content.forEach((item) => {
            if (item.mapping) {
                maps[item.id] = item.mapping;
            }
        });

        setMappingData(maps);
        localStorage.setItem(
            "mapping",
            CryptoJS.AES.encrypt(JSON.stringify(maps), "secret").toString()
        );
    }, [session, current.data.session]);

    const getSoal = () => {
        if (
            !session.data ||
            session.data.length === 0 ||
            session.data.soal === null
        )
            return "";
        if ("exam" !== session.data[current.data.session]?.type) return "";

        return session.data[current.data.session].content[current.data.soal]
            ?.soal;
    };

    const getAnswer = (id) => {
        if (
            !session.data ||
            session.data.length === 0 ||
            current.data.session === null ||
            current.data.soal === null
        )
            return "";
        if ("exam" !== session.data[current.data.session]?.type) return "";

        const stored = getStoredAnswer();
        let answer = stored || answerData || {};

        if (id !== undefined) {
            return answer[id] ? answer[id] : "";
        }

        const currentQuestion = session.data[current.data.session]?.content?.[current.data.soal];
        if (!currentQuestion) return "";

        return answer[currentQuestion.id] || "";
    };

    const answering = (idSoal, jawaban, answerCount = 1) => {
        const stored = getStoredAnswer();
        let answer = { ...(stored || answerData || {}) };

        // Handle different data types based on question type
        if (typeof jawaban === 'object' && jawaban !== null) {
            // For dimensional (most/least), kraeplin (grid), drawing (image), potensi-kerja (columns)
            answer[idSoal] = jawaban;
        } else if (answerCount > 1) {
            // Multiple choice (array)
            if (answer[idSoal] === undefined) {
                answer[idSoal] = [];
            }

            if (answer[idSoal].includes(jawaban)) {
                answer[idSoal].splice(answer[idSoal].indexOf(jawaban), 1);
            } else {
                if (answer[idSoal].length === answerCount) {
                    return;
                } else {
                    answer[idSoal].push(jawaban);
                }
            }
        } else {
            // Single choice (string/number)
            answer[idSoal] = jawaban;
        }

        setAnswerData({ ...answer });
        localStorage.setItem(
            "answer",
            CryptoJS.AES.encrypt(JSON.stringify(answer), "secret").toString()
        );
    };

    const finishing = (explicitAnswer) => {
        const params = new URLSearchParams(window.location.search);
        const answersToSubmit = explicitAnswer || getStoredAnswer() || answerData || {};
        const mappingToSubmit = getStoredMapping() || mappingData || {};
        const currentSession = session.data?.[current.data.session];

        BaseEndpoint.instance()
            .post(`/exam/${user.version}`, {
                id: params.get("id"),
                token: user.token,
                type: currentSession?.name,
                psychotest_id: currentSession?.psychotest_id,
                answer: answersToSubmit,
                mapping: mappingToSubmit,
            })
            .then((response) => {
                if (response.status === 200) {
                    localStorage.removeItem("answer");
                    localStorage.removeItem("mapping");
                    setAnswerData(null);
                    setMappingData(null);

                    // Check if there are more sessions
                    let isLastSession = nextSession();

                    // Only redirect to finish if explicitly true (no more sessions)
                    if (isLastSession === true) {
                        localStorage.removeItem("session");
                        router.push(
                            user.version === "tes-akademik"
                                ? "/app"
                                : "/app/finish"
                        );
                    }
                    // If false, nextSession already handled moving to next test
                }
            })
            .catch((error) => {
                console.error('Error saving answers:', error);
                localStorage.removeItem("answer");
                localStorage.removeItem("mapping");
                setAnswerData(null);
                setMappingData(null);

                let isLastSession = nextSession();
                if (isLastSession === true) {
                    localStorage.removeItem("session");
                    router.push(
                        user.version === "tes-akademik"
                            ? "/app"
                            : "/app/finish"
                    );
                }
            });
    };

    // Detect psychotest type for special-potential-test
    const getPsychotestType = () => {
        if (user.version !== 'spesial-potential-test') return null;
        if (!session.data || session.data[current.data.session]?.type !== 'exam') return null;

        const currentQuestion = session.data[current.data.session].content[current.data.soal];
        return currentQuestion?.psychotest_type || 'standard';
    };

    // Render question based on type (for spesial-potential-test only)
    const renderSpecialPotentialQuestion = () => {
        const currentQuestion = session.data[current.data.session].content[current.data.soal];
        const questionType = getPsychotestType();
        const currentAnswer = getAnswer();

        const commonProps = {
            question: currentQuestion,
            answer: currentAnswer,
            onAnswer: answering,
            disabled: false
        };

        switch (questionType) {
            case 'standard':
                return <QuestionStandard {...commonProps} />;

            case 'dimensional':
                return <QuestionDimensional {...commonProps} />;

            case 'personality-scale':
                return <QuestionPersonality {...commonProps} />;

            case 'kraeplin': {
                // Pass all questions in this session (one lembar = all sub-columns)
                const currentSessionData = session.data[current.data.session] || {};
                const allSessionQuestions = currentSessionData.content || [];
                return <QuestionKraeplin
                    {...commonProps}
                    allQuestions={allSessionQuestions}
                    getAnswer={(qId) => getAnswer(qId)}
                    countdown={countdown}
                    timeRemaining={countdown.duration}
                    inkolom={currentSessionData.inkolom || 0}
                    changekolom={currentSessionData.changekolom || 0}
                    onFinish={() => finishing()}
                    onNextQuestion={() => {
                        const totalQuestions = allSessionQuestions.length;
                        const nextSoal = current.data.soal + 1;
                        if (nextSoal < totalQuestions) {
                            current.set({ ...current.data, soal: nextSoal });
                        }
                    }}
                />;
            }

            case 'drawing':
                return <QuestionDrawing {...commonProps} />;

            case 'potensi-kerja':
                // Use SPT component for spesial-potential-test, PT component for potential-test
                return <QuestionSPTPotensiKerja {...commonProps} />;

            default:
                return <QuestionStandard {...commonProps} />;
        }
    };

    return (
        <div className="row mx-0 mt-4 px-0 col-md-12">
            <div className="col-md-3 px-0">
                <div className="card">
                    <div className="card-body">
                        <h5 className="card-title border-bottom pb-3">
                            Navigasi
                        </h5>
                        <div className="row px-2 justify-content-center tw-max-h-[430px] tw-overflow-y-auto">
                            {session.data &&
                                session.data[current.data.session] !==
                                undefined &&
                                session.data[current.data.session].type ===
                                "exam" &&
                                (() => {
                                    const isKraeplin = getPsychotestType() === 'kraeplin';

                                    if (isKraeplin) {
                                        // For Kraeplin: show simple sheet info since all columns are displayed at once
                                        const content = session.data[current.data.session].content;
                                        const sessionName = session.data[current.data.session].name || '';
                                        const lembarMatch = sessionName.match(/Lembar (\d+)/);
                                        const lembarNum = lembarMatch ? lembarMatch[1] : null;

                                        return (
                                            <div className="col-12 text-center">
                                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: '#fff5eb', border: '1px solid #fde8d0' }}>
                                                    <i className="fas fa-file-alt text-warning mb-1" style={{ fontSize: '1.5rem' }}></i>
                                                    <div className="font-weight-bold" style={{ color: '#f56409' }}>
                                                        {lembarNum ? `Lembar ${lembarNum}` : 'Kraeplin'}
                                                    </div>
                                                    <small className="text-muted">
                                                        {content.length} kolom ditampilkan
                                                    </small>
                                                </div>
                                                {content.map((item, idx) => {
                                                    const answered = getAnswer(item.id);
                                                    const hasAnswer = answered && typeof answered === 'object' && Object.keys(answered).length > 0;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="d-inline-block m-1 p-1 text-center border rounded"
                                                            style={{
                                                                width: '36px',
                                                                fontSize: '0.75rem',
                                                                backgroundColor: hasAnswer ? '#d4edda' : '#f8f9fa',
                                                                color: hasAnswer ? '#155724' : '#6c757d',
                                                                borderColor: hasAnswer ? '#c3e6cb' : '#dee2e6',
                                                            }}
                                                        >
                                                            K{idx + 1}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }

                                    return session.data[current.data.session].content.map(
                                        (item, index) => (
                                            <div
                                                key={index}
                                                onClick={() => {
                                                    current.set({
                                                        ...current.data,
                                                        soal: index,
                                                    });
                                                }}
                                                className={`col-md-2 m-1 p-2 text-center border rounded ${current.data.soal === index
                                                    ? "bg-primary border-primary text-white"
                                                    : ""
                                                    }`}
                                                style={{
                                                    cursor: "pointer",
                                                    backgroundColor: getAnswer(
                                                        session.data[
                                                            current.data.session
                                                        ].content[index].id
                                                    )
                                                        ? "#f56409"
                                                        : "",
                                                    opacity:
                                                        getAnswer(
                                                            session.data[
                                                                current.data.session
                                                            ].content[index].id
                                                        ) &&
                                                            current.data.soal !== index
                                                            ? 0.65
                                                            : "",
                                                    color: getAnswer(
                                                        session.data[
                                                            current.data.session
                                                        ].content[index].id
                                                    )
                                                        ? "#fff"
                                                        : "",
                                                }}
                                            >
                                                {index + 1}
                                            </div>
                                        )
                                    );
                                })()}
                        </div>
                    </div>
                </div>
            </div>
            <div className="col-md-9 px-0">
                <div className="col-md-12 ps-3">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="card-title border-bottom pb-3">
                                Soal
                            </h5>
                            <div
                                className="py-2"
                                style={{ minHeight: "180px" }}
                            >
                                {/* Check if spesial-potential-test and render type-specific components */}
                                {user.version === 'spesial-potential-test' &&
                                    session.data &&
                                    session.data[current.data.session]?.type === 'exam' ? (
                                    renderSpecialPotentialQuestion()
                                ) : (
                                    <>
                                        {/* Original generic rendering for other test types */}
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: getSoal(),
                                            }}
                                        ></div>
                                        <div className="mt-4 mb-3">
                                            {session.data &&
                                                session.data[current.data.session] !==
                                                undefined &&
                                                session.data[current.data.session]
                                                    .type === "exam" &&
                                                current.data.soal !== null &&
                                                (() => {
                                                    let pilihan =
                                                        session.data[
                                                            current.data.session
                                                        ].content[current.data.soal]
                                                            ?.pilihan || null;
                                                    if (!pilihan) return null;

                                                    if (typeof pilihan === 'string') {
                                                        try {
                                                            pilihan = JSON.parse(pilihan);
                                                        } catch (e) {
                                                            pilihan = {};
                                                        }
                                                    }

                                                    if (Array.isArray(pilihan)) {
                                                        const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
                                                        const obj = {};
                                                        pilihan.forEach((val, idx) => {
                                                            const k = letters[idx] || String(idx + 1);
                                                            obj[k] = val;
                                                        });
                                                        pilihan = obj;
                                                    }

                                                    if (!pilihan || typeof pilihan !== 'object') return null;

                                                    return Object.keys(pilihan).map(
                                                        (key, index) => (
                                                            <label
                                                                key={index}
                                                                className={`d-block border px-3 py-2 mb-3 rounded-2 ${Array.isArray(
                                                                    getAnswer()
                                                                )
                                                                    ? getAnswer().includes(
                                                                        key
                                                                    )
                                                                        ? "bg-primary border-primary text-white"
                                                                        : ""
                                                                    : getAnswer() ===
                                                                        key
                                                                        ? "bg-primary border-primary text-white"
                                                                        : ""
                                                                    }`}
                                                                htmlFor={`id${key}`}
                                                                style={{
                                                                    position:
                                                                        "relative",
                                                                    opacity:
                                                                        Array.isArray(
                                                                            getAnswer()
                                                                        )
                                                                            ? getAnswer().includes(
                                                                                key
                                                                            )
                                                                                ? 1
                                                                                : 0.8
                                                                            : getAnswer() ===
                                                                                key
                                                                                ? 1
                                                                                : 0.8,
                                                                }}
                                                            >
                                                                <input
                                                                    onChange={() =>
                                                                        answering(
                                                                            session
                                                                                .data[
                                                                                current
                                                                                    .data
                                                                                    .session
                                                                            ].content[
                                                                                current
                                                                                    .data
                                                                                    .soal
                                                                            ].id,
                                                                            key,
                                                                            session
                                                                                .data[
                                                                                current
                                                                                    .data
                                                                                    .session
                                                                            ].content[
                                                                                current
                                                                                    .data
                                                                                    .soal
                                                                            ].jj || 1
                                                                        )
                                                                    }
                                                                    className="form-check-input"
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        top: 0,
                                                                        left: 0,
                                                                        height: 0,
                                                                        width: 0,
                                                                        opacity: 0,
                                                                    }}
                                                                    type="checkbox"
                                                                    value={key}
                                                                    id={`id${key}`}
                                                                    name="answer"
                                                                    checked={
                                                                        getAnswer() ===
                                                                            key
                                                                            ? true
                                                                            : false
                                                                    }
                                                                />
                                                                <div className="d-flex">
                                                                    <span className="me-2">
                                                                        {key + ". "}
                                                                    </span>
                                                                    <span
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: pilihan[
                                                                                key
                                                                            ],
                                                                        }}
                                                                    ></span>
                                                                </div>
                                                            </label>
                                                        )
                                                    );
                                                })()}
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="d-flex justify-content-end mt-2">
                                <button
                                    onClick={() => {
                                        if (current.data.session === null)
                                            return;
                                        if (current.data.soal === 0) return;

                                        let prevSoal = current.data.soal - 1;
                                        current.set({
                                            ...current.data,
                                            soal: prevSoal,
                                        });
                                    }}
                                    className="btn btn-secondary btn-md rounded-3 ms-2"
                                    disabled={
                                        current.data.soal === 0 ? true : false
                                    }
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => {
                                        if (current.data.session === null)
                                            return;
                                        if (
                                            current.data.soal ===
                                            session.data[current.data.session]
                                                .content.length -
                                            1
                                        )
                                            return;

                                        let nextSoal = current.data.soal + 1;
                                        current.set({
                                            ...current.data,
                                            soal: nextSoal,
                                        });
                                    }}
                                    className="btn btn-primary btn-md text-white rounded-3 ms-2"
                                    disabled={
                                        session &&
                                            session.data[current.data.session] &&
                                            session.data[current.data.session]
                                                .content &&
                                            current.data.soal ===
                                            session.data[current.data.session]
                                                .content.length -
                                            1
                                            ? true
                                            : false
                                    }
                                >
                                    Next
                                </button>
                                {session &&
                                    session.data[current.data.session] &&
                                    session.data[current.data.session]
                                        .content &&
                                    current.data.soal ===
                                    session.data[current.data.session]
                                        .content.length -
                                        1 && (
                                        <button
                                            onClick={() => {
                                                confirm(
                                                    "Anda yakin ingin menyelesaikan sesi ini?"
                                                ) && finishing();
                                            }}
                                            className="btn btn-primary btn-md text-white rounded-3 ms-2"
                                        >
                                            Finish
                                        </button>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
